import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context';
import portalService from '../../services/portalService';
import PortalShell from './PortalShell';

const INVITE_STORAGE_KEY = 'portal:inviteCode';

/**
 * /portal — state-aware landing.
 *
 * Routes the viewer based on backend state:
 *   "anonymous" / "not_applied" / "no_profile" → render landing copy
 *   "pending"                                  → "in review" panel
 *   "declined" / "not_yet"                     → quiet lockout panel
 *   "accepted"                                 → bounce to /portal/home
 *
 * Mocks: Portal_Landing.png. Hero "You found us." in Times New Roman, body
 * paragraph max 560px, single REQUEST A SEAT CTA, italic whisper at the foot.
 */
export default function PortalLandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [invitePreview, setInvitePreview] = useState(null);

  // If the URL has ?invite=CODE, stash it in sessionStorage so it survives
  // sign-up / login redirects, and fetch the inviter preview for the banner.
  useEffect(() => {
    const code = searchParams.get('invite');
    if (code) {
      try { sessionStorage.setItem(INVITE_STORAGE_KEY, code); } catch {}
    }
    const stashed = code || (() => { try { return sessionStorage.getItem(INVITE_STORAGE_KEY); } catch { return null; } })();
    if (stashed) {
      portalService.previewPortalInvite(stashed).then((r) => {
        if (r.success && r.data) setInvitePreview(r.data);
      });
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    portalService.getState().then((r) => {
      if (cancelled) return;
      if (r.success) {
        setState(r.data);
        if (r.data?.state === 'accepted') navigate('/noir/home', { replace: true });
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleApplyClick = () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/portal/apply');
      return;
    }
    if (state?.state === 'no_profile') {
      navigate('/onboard');
      return;
    }
    navigate('/noir/apply');
  };

  // Status panels for returning applicants
  if (state?.state === 'pending') {
    return (
      <PortalShell viewerState={state.state}>
        <div style={{ maxWidth: 720, margin: '120px auto', padding: '0 24px' }}>
          <p className="portal-label" style={{ marginBottom: 14 }}>APPLICATION SUBMITTED</p>
          <h1 className="portal-hero" style={{ marginBottom: 18 }}>In review.</h1>
          <p className="portal-prose">
            We've got it. We read every application. Most are reviewed within two weeks.
          </p>
        </div>
      </PortalShell>
    );
  }

  if (state?.state === 'declined' || state?.state === 'not_yet') {
    const isNotYet = state.state === 'not_yet';
    const allowedDate = state.canReapplyAt ? new Date(state.canReapplyAt) : null;
    return (
      <PortalShell viewerState={state.state}>
        <div style={{ maxWidth: 720, margin: '120px auto', padding: '0 24px' }}>
          <p className="portal-label" style={{ marginBottom: 14 }}>
            {isNotYet ? 'NOT YET' : 'NOT THIS TIME'}
          </p>
          <h1 className="portal-hero" style={{ marginBottom: 18 }}>
            {isNotYet ? 'Soon, maybe.' : 'Thank you for applying.'}
          </h1>
          <p className="portal-prose">
            {isNotYet
              ? 'We loved reading your application. The moment isn\'t right — but try us again later.'
              : 'We say yes to some, no to most. We mean it kindly.'}
          </p>
          {allowedDate && (
            <p className="portal-whisper" style={{ marginTop: 24 }}>
              You can apply again on {allowedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
            </p>
          )}
        </div>
      </PortalShell>
    );
  }

  // Default — public landing
  return (
    <PortalShell viewerState={state?.state}>
      <div style={{ maxWidth: 720, margin: '90px auto 60px', padding: '0 24px' }}>
        {invitePreview && !invitePreview.revoked && !invitePreview.expired && !invitePreview.used && (
          <div className="portal-card" style={{ padding: 18, marginBottom: 28, borderColor: '#C9A24B', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="portal-avatar" style={{ width: 48, height: 48 }}>
              {invitePreview.inviterAvatarUrl ? <img src={invitePreview.inviterAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="portal-mono portal-gold" style={{ fontSize: 10, marginBottom: 4 }}>YOU WERE INVITED</p>
              <p className="portal-serif" style={{ fontStyle: 'italic', color: '#ECE7DA' }}>
                <strong>{invitePreview.inviterDisplayName}</strong>
                {invitePreview.inviterHandle ? <span className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)', marginLeft: 6 }}>@{invitePreview.inviterHandle}</span> : null}
                {' '}invited you in.
              </p>
              {invitePreview.label && <p className="portal-whisper" style={{ marginTop: 4 }}>"{invitePreview.label}"</p>}
            </div>
          </div>
        )}
        {invitePreview && (invitePreview.expired || invitePreview.used || invitePreview.revoked) && (
          <div className="portal-card" style={{ padding: 14, marginBottom: 28 }}>
            <p className="portal-whisper">
              That invite link is {invitePreview.expired ? 'expired' : invitePreview.used ? 'already claimed' : 'no longer active'}. You can still apply — read on.
            </p>
          </div>
        )}
        <h1 className="portal-hero" style={{ marginBottom: 32 }}>
          <em style={{ fontStyle: 'italic' }}>You</em> found us.
        </h1>
        <p className="portal-prose" style={{ marginBottom: 40, maxWidth: '44ch' }}>
          Portal Noir is an invite-only network of remarkable people, like you.
        </p>
        <button type="button" onClick={handleApplyClick} className="portal-btn">
          REQUEST A SEAT
        </button>
      </div>
    </PortalShell>
  );
}
